import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Button from '../../_common/Form/Buttons/Button';
import classes from './reports.module.scss';
import { RangeDatePicker } from '../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import apiConfig from '../../../config';
import AccountHelpers from '../../../utils/accountHelpers';
import ManufacturerReportModal from './ManufacturerReportModal';
import Api from '../../../api/api';
import InputField from '../../_common/Form/TextFields/InputField';
import SelectWithSearch from '../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import Loader from '../../_common/Loader/Loader';
import TableAndPagination from '../../_common/TableAndPagination/TableAndPagination';
import Utils from '../../../utils/utils';
import { useQuery } from '../../../utils/hooks/useQuery';

const { API_URL } = apiConfig;

const tableHeader = [
  { name: 'Physician', path: 'physician' },
  { name: 'Patient', path: 'patient' },
  { name: 'Medication', path: 'medicationName' },
  { name: 'Location', path: 'location' },
  { name: 'Quantity', path: 'quantity' },
  { name: 'Dosage', path: 'dosage' },
  { name: 'Authorization #', path: 'authNo' },
  { name: 'Confirmation Date', path: 'confirmationDate' },
  { name: 'Confirmation Number', path: 'confirmationNo' },
  { name: 'Counselor', path: 'counselor' },
  { name: 'Checklist', path: 'checklist' },
  { name: 'Counsel Date', path: 'counselDate' },
  { name: 'Dispense Date', path: 'dispense' },
  { name: 'Ship Date', path: 'shipOn' },
  { name: 'Delivery Method', path: 'deliveryMethod' },
  { name: 'New Start', path: 'newStart' },
  { name: 'ADE', path: 'ade' },
  { name: 'Med Guide', path: 'medGuide' },
  { name: 'Risk Category', path: 'riskCategory' },
];

const defaultSelectData = {
  searchValue: '',
  options: [],
  loading: false,
};

const formatData = (requestData) => {
  const data = { ...requestData };

  if (data.start && !data.end) {
    data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
  }

  if (data.dispenseStart && !data.dispenseEnd) {
    data.dispenseEnd = moment(data.dispenseStart).add(1, 'day').format('YYYY-MM-DD');
  }

  return data;
};

const Reports = () => {
  const { query, setQuery } = useQuery();

  const isManufacturerUsers = AccountHelpers.isInRole('Manufacturer Users');

  const navigate = useNavigate();

  const timeout = useRef();
  const firstLoad = useRef(!!isManufacturerUsers);

  const [filters, setFilters] = useState({
    page: query.page || 1,
    physician: query.physician || '',
    city: query.city || '',
    dosage: query.dosage || '',
    group: query.group || '',
    diagnosis: query.diagnosis || '',
    payor: query.payor || '',
    location: query.location || '',
    limit: query.limit || 15,
  });

  const [groupData, setGroupData] = useState(defaultSelectData);

  const [diagnosisData, setDiagnosisData] = useState(defaultSelectData);

  const [payorData, setPayorData] = useState(defaultSelectData);

  const [locationData, setLocationData] = useState(defaultSelectData);

  const [date, setDate] = useState({
    start: isManufacturerUsers ? (query.start || moment().startOf('month').format('YYYY-MM-DD')) : '',
    end: isManufacturerUsers ? (query.end || moment().format('YYYY-MM-DD')) : '',
  });

  const { start, end } = date;

  const [dispenseDate, setDispenseDate] = useState({
    dispenseStart: query.dispenseStart || '',
    dispenseEnd: query.dispenseEnd || '',
  });

  const { dispenseStart, dispenseEnd } = dispenseDate;

  const [downloadLoadType, setDownloadLoadType] = useState('');

  const [modalInfo, setModalInfo] = useState({ isOpen: false });

  const [reportsData, setReportsData] = useState({
    list: [],
    totalPages: 1,
  });
  const [loading, loadingToggle] = useState(false);

  const getReport = async (e) => {
    e?.preventDefault();

    loadingToggle(true);

    const sendingData = Utils.deleteEmptyKeys({
      ...date,
      ...dispenseDate,
      ...filters,
    });

    setQuery(sendingData);

    const { data } = await Api.getManufacturerUserReport(formatData(sendingData));

    setReportsData({
      list: data.data,
      totalPages: data._meta.pageCount,
    });

    firstLoad.current = false;
    loadingToggle(false);
  };

  useEffect(() => {
    if (isManufacturerUsers) {
      getReport();
    }
  }, [filters.limit]);

  const changeFilters = (path, value) => {
    setFilters((prev) => ({ ...prev, [path]: value }));

    if (path === 'group') {
      setGroupData((prev) => ({ ...prev, searchValue: value }));
    } else if (path === 'diagnosis') {
      setDiagnosisData((prev) => ({ ...prev, searchValue: value }));
    } else if (path === 'payor') {
      setPayorData((prev) => ({ ...prev, searchValue: value }));
    } else if (path === 'location') {
      setLocationData((prev) => ({ ...prev, searchValue: value }));
    }
  };

  const changePageData = async (path, count) => {
    setFilters((prev) => ({ ...prev, page: 1, [path]: count }));
  };

  const clearFilters = async () => {
    loadingToggle(true);

    const resetDate = {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
    };

    setQuery(resetDate);

    setDate(resetDate);

    setDispenseDate({
      dispenseStart: '',
      dispenseEnd: '',
    });

    setFilters((prev) => ({
      page: 1,
      physician: '',
      city: '',
      dosage: '',
      group: '',
      diagnosis: '',
      payor: '',
      location: '',
      limit: prev.limit,
    }));

    setGroupData(defaultSelectData);
    setDiagnosisData(defaultSelectData);
    setPayorData(defaultSelectData);
    setLocationData(defaultSelectData);

    const { data } = await Api.getManufacturerUserReport(resetDate);

    setReportsData({
      list: data.data,
      totalPages: data._meta.pageCount,
    });

    loadingToggle(false);
  };

  const searchGroup = (searchValue) => {
    setGroupData((prev) => ({ ...prev, searchValue }));

    clearTimeout(timeout.current);

    if (searchValue.trim()) {
      setGroupData((prev) => ({ ...prev, loading: true }));

      timeout.current = setTimeout(async () => {
        const { data: { data } } = await Api.getReportSearchList({ group: searchValue });

        setGroupData((prev) => ({
          ...prev,
          options: data,
          loading: false,
        }));
      }, 500);
    } else {
      setGroupData((prev) => ({ ...prev, options: [], loading: false }));
    }
  };

  const searchDiagnosis = (searchValue) => {
    setDiagnosisData((prev) => ({ ...prev, searchValue }));

    clearTimeout(timeout.current);

    if (searchValue.trim()) {
      setDiagnosisData((prev) => ({ ...prev, loading: true }));

      timeout.current = setTimeout(async () => {
        const { data: { data } } = await Api.getReportSearchList({ diagnosis: searchValue });

        setDiagnosisData((prev) => ({
          ...prev,
          options: data,
          loading: false,
        }));
      }, 500);
    } else {
      setDiagnosisData((prev) => ({ ...prev, options: [], loading: false }));
    }
  };

  const searchPayor = (searchValue) => {
    setPayorData((prev) => ({ ...prev, searchValue }));

    clearTimeout(timeout.current);

    if (searchValue.trim()) {
      setPayorData((prev) => ({ ...prev, loading: true }));

      timeout.current = setTimeout(async () => {
        const { data: { data } } = await Api.getReportSearchList({ payor: searchValue });

        setPayorData((prev) => ({
          ...prev,
          options: data,
          loading: false,
        }));
      }, 500);
    } else {
      setPayorData((prev) => ({ ...prev, options: [], loading: false }));
    }
  };

  const searchLocation = (searchValue) => {
    setLocationData((prev) => ({ ...prev, searchValue }));

    clearTimeout(timeout.current);

    if (searchValue.trim()) {
      setLocationData((prev) => ({ ...prev, loading: true }));

      timeout.current = setTimeout(async () => {
        const { data: { data } } = await Api.getReportSearchList({ location: searchValue });

        setLocationData((prev) => ({
          ...prev,
          options: data,
          loading: false,
        }));
      }, 500);
    } else {
      setLocationData((prev) => ({ ...prev, options: [], loading: false }));
    }
  };

  const onChangeDate = useCallback((currentDate) => {
    setDate({ start: currentDate[0], end: currentDate[1] });
  }, []);

  const onChangeDispenseDate = useCallback((currentDate) => {
    setDispenseDate({ dispenseStart: currentDate[0], dispenseEnd: currentDate[1] });
  }, []);

  const download = useCallback(async (link, title) => {
    if (!start) {
      toast.error('Please choose date');
    } else {
      setDownloadLoadType(title);
      const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');

      const reportLink = `${link}?start=${start}&end=${end || tomorrow}&email=${AccountHelpers.getUser().email}`;

      await axios(reportLink, {
        headers: {
          Authorization: `Bearer ${AccountHelpers.getToken()}`,
        },
      });

      toast.success(`${title} will be generated in background and will be sent you via email`);

      setDownloadLoadType('');
    }
  }, [date]);

  const reports = useMemo(() => [{
    title: 'OptumRx Shipping Accuracy Report',
    link: `${API_URL}/api/dashboards/report/optum`,
  },

  {
    title: 'URAC Turn Around Time Report',
    link: `${API_URL}/api/dashboards/report/urac`,
  },

  {
    title: 'Genentech Report',
    link: `${API_URL}/api/dashboards/report/genentech`,
  },

  {
    title: 'Prescription Export Report',
    link: `${API_URL}/api/dashboards/report/prescription`,
  },

  {
    title: 'Prime Therapeutics Report',
    link: `${API_URL}/api/dashboards/report/prime`,
  },
  ], []);

  return (
    <div className={`${classes.wrapper} ${isManufacturerUsers ? classes.manufacturer_user : ''}`}>
      {firstLoad.current
        ? <Loader />

        : (
          <>
            {isManufacturerUsers
              ? (
                <form className={classes.filters} onSubmit={getReport}>
                  <div className={classes.date_wrapper}>
                    <p>Received</p>

                    <RangeDatePicker
                      currentDate={date}
                      onChangeDate={onChangeDate}
                    />
                  </div>

                  <div className={classes.date_wrapper}>
                    <p>Dispense Date</p>

                    <RangeDatePicker
                      currentDate={{
                        start: dispenseDate.dispenseStart,
                        end: dispenseDate.dispenseEnd,
                      }}
                      emptyAfterClear
                      onChangeDate={onChangeDispenseDate}
                    />
                  </div>

                  <div className={classes.physician}>
                    <InputField
                      label="Physician"
                      onChange={(value) => changeFilters('physician', value)}
                      value={filters.physician}
                    />
                  </div>

                  <div className={classes.physician}>
                    <InputField
                      label="City"
                      onChange={(value) => changeFilters('city', value)}
                      value={filters.city}
                    />
                  </div>

                  <div className={classes.physician}>
                    <InputField
                      label="Dosage"
                      onChange={(value) => changeFilters('dosage', value)}
                      value={filters.dosage}
                    />
                  </div>

                  <div className={classes.physician}>
                    <SelectWithSearch
                      label="Group"
                      onChange={(value) => searchGroup(value)}
                      loading={groupData.loading}
                      options={groupData.options}
                      onSelectOption={(option) => changeFilters('group', option.name)}
                      searchValue={groupData.searchValue}
                      optionPathname="name"
                      optionKey=""
                      zIndex={31}
                    />
                  </div>

                  <div className={classes.physician}>
                    <SelectWithSearch
                      label="Diagnosis"
                      onChange={(value) => searchDiagnosis(value)}
                      loading={diagnosisData.loading}
                      options={diagnosisData.options}
                      onSelectOption={(option) => changeFilters('diagnosis', option.name)}
                      searchValue={diagnosisData.searchValue}
                      optionPathname="name"
                      optionKey=""
                      zIndex={31}
                    />
                  </div>

                  <div className={classes.physician}>
                    <SelectWithSearch
                      label="Payor"
                      onChange={(value) => searchPayor(value)}
                      loading={payorData.loading}
                      options={payorData.options}
                      onSelectOption={(option) => changeFilters('payor', option.name)}
                      searchValue={payorData.searchValue}
                      optionPathname="name"
                      optionKey=""
                      zIndex={31}
                    />
                  </div>

                  <div className={classes.physician}>
                    <SelectWithSearch
                      label="Location"
                      onChange={(value) => searchLocation(value)}
                      loading={locationData.loading}
                      options={locationData.options}
                      onSelectOption={(option) => changeFilters('location', option.name)}
                      searchValue={locationData.searchValue}
                      optionPathname="name"
                      optionKey=""
                      zIndex={31}
                    />
                  </div>

                  <Button
                    className={classes.update_btn}
                    title="Update"
                    type="submit"
                  />

                  <Button
                    className={classes.clear_btn}
                    title="Clear"
                    onClick={clearFilters}
                    clearButton
                  />

                  <Button
                    className={classes.download}
                    downloadButton
                    reverseColor
                    disabled={!reports.length}
                    title="Configure and Download"
                    onClick={() => setModalInfo({
                      isOpen: true,
                      link: `${API_URL}/api/dashboards/report/manufacturer`,
                    })}
                  />
                </form>
              )

              : (
                <>
                  {' '}
                  <div className={classes.calendar_wrapper}>
                    <div className={classes.title}>Select date range</div>

                    <RangeDatePicker
                      currentDate={date}
                      onChangeDate={onChangeDate}
                    />
                  </div>

                  <div className={classes.report_download_wrapper}>
                    {reports.map(({
                      title, link, type, btnTitle = 'Download', className,
                    }) => (
                      <div key={title}>
                        <div className={classes.title}>{title}</div>

                        <Button
                          className={className}
                          downloadButton
                          reverseColor
                          title={btnTitle}
                          onClick={() => download(link, title, type)}
                          loading={downloadLoadType === title}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

            <div className={classes.table_wrapper}>
              {loading
                ? <Loader />

                : (
                  <TableAndPagination
                    onChange={changePageData}
                    onClick={(id) => navigate(`/scripts/${id}`)}
                    data={reportsData.list}
                    header={tableHeader}
                    totalPage={+reportsData.totalPages}
                    page={+filters.page}
                    limit={+filters.limit}
                  />
                )}
            </div>

            <ManufacturerReportModal
              modalInfo={modalInfo}
              filters={{
                start,
                end: start && !end ? moment(start).add(1, 'day').format('YYYY-MM-DD') : end,
                dispenseStart,
                dispenseEnd: dispenseStart && !dispenseEnd ? moment(dispenseStart).add(1, 'day').format('YYYY-MM-DD') : dispenseEnd,
                ...filters,
              }}
              onClose={() => setModalInfo({ isOpen: false })}
            />
          </>
        )}
    </div>
  );
};

export default Reports;
